/**
 * SE-3118: pure helpers for config-driven identifiers (forms + bulk invite).
 * Messages match the backend (api/lib/network-identifiers) so client and server
 * errors read the same.
 */

/** Values keyed by identifier key, kept as text so leading zeros survive. */
export type IdentifierValues = Record<string, string>;

export const IDENTIFIER_KEY_NPI = "npi";
export const IDENTIFIER_KEY_LEARN = "appd_learn_id";

/** True when value matches the identifier's regex; a missing or malformed regex never blocks. */
export const matchesIdentifierRegex = (
  regex: string | null | undefined,
  value: string
): boolean => {
  if (!regex) return true;
  try {
    return new RegExp(regex).test(value);
  } catch {
    return true;
  }
};

export const identifierRequiredMessage = (displayName: string) =>
  `${displayName} is required`;
export const identifierInvalidMessage = (displayName: string) =>
  `${displayName} is invalid`;

/** Required + format check for one value; null when it passes. */
export const identifierValueError = (
  item: { displayName: string; required: boolean; validationRegex: string | null },
  raw: unknown
): string | null => {
  const value = raw == null ? "" : String(raw).trim();
  if (!value) return item.required ? identifierRequiredMessage(item.displayName) : null;
  return matchesIdentifierRegex(item.validationRegex, value)
    ? null
    : identifierInvalidMessage(item.displayName);
};

// One digit token: a digit class like [0-9] / [12], \d or a literal digit, with an optional quantifier.
const DIGIT_TOKEN = String.raw`(?:\[[0-9-]+\]|\\d|[0-9])(?:\{\d+(?:,\d*)?\}|[+*?])?`;
const DIGITS_ONLY = new RegExp(`^\\^?(?:${DIGIT_TOKEN})+\\$?$`);

/** True for regexes that only accept digits (shows a numeric keypad on mobile). */
export const isDigitsOnlyRegex = (regex: string | null | undefined) =>
  !!regex && DIGITS_ONLY.test(regex);

/**
 * Bulk CSV column for an identifier: camelCase of its key (aamc_id → aamcId).
 * LEARN keeps its existing "simplLearnId" column so current templates still work.
 * Keys are lowercase snake_case, so identifierKeyForColumn reverses this exactly.
 */
export const identifierColumn = (key: string): string =>
  key === IDENTIFIER_KEY_LEARN
    ? "simplLearnId"
    : key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

/** Inverse of identifierColumn: CSV column → identifier key (aamcId → aamc_id). */
export const identifierKeyForColumn = (column: string): string =>
  column === "simplLearnId"
    ? IDENTIFIER_KEY_LEARN
    : column.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

const firstAllowed = (cls: string, prefer: string[]): string => {
  const allowed = (ch: string) => {
    for (let i = 0; i < cls.length; i++) {
      if (cls[i + 1] === "-" && cls[i + 2]) {
        if (ch >= cls[i]! && ch <= cls[i + 2]!) return true;
        i += 2;
      } else if (cls[i] === ch) return true;
    }
    return false;
  };
  return prefer.find(allowed) ?? cls[0]!;
};

const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz".split("");

/**
 * Best-guess example value for an identifier's regex, for the bulk template's
 * example rows (e.g. ^[0-9]{8}$ → "10000000", variant 1 → "10000001").
 * Handles anchors, character classes, \d, \w, literals and quantifiers; returns
 * null for anything else (groups, alternation) so the caller can fall back.
 */
export const exampleFromRegex = (
  regex: string | null | undefined,
  variant = 0
): string | null => {
  if (!regex) return null;
  const src = regex.replace(/^\^/, "").replace(/\$$/, "");
  let out = "";
  let digitCount = 0;
  let i = 0;
  while (i < src.length) {
    let unit: "digit" | "char";
    let cls = "";
    const ch = src[i]!;
    if (ch === "[") {
      const end = src.indexOf("]", i);
      if (end < 0 || src[i + 1] === "^") return null;
      cls = src.slice(i + 1, end);
      unit = /^[0-9-]+$/.test(cls) ? "digit" : "char";
      i = end + 1;
    } else if (ch === "\\") {
      const esc = src[i + 1];
      if (esc === "d") unit = "digit";
      else if (esc === "w") {
        unit = "char";
        cls = "A-Za-z0-9_";
      } else if (esc && !/[a-zA-Z]/.test(esc)) {
        unit = "char";
        cls = esc;
      } else return null;
      i += 2;
    } else if (/[()|.+*?{}]/.test(ch)) {
      return null;
    } else {
      unit = "char";
      cls = ch;
      i += 1;
    }

    let count = 1;
    const q = src.slice(i).match(/^(\{(\d+)(?:,\d*)?\}|[+*?])/);
    if (q) {
      count = q[2] ? Number(q[2]) : q[1] === "+" ? 1 : 0;
      i += q[1]!.length;
    }
    for (let n = 0; n < count; n++) {
      if (unit === "digit") {
        const range = cls || "0-9";
        out += firstAllowed(range, digitCount === 0 ? ["1", ...DIGITS] : DIGITS);
        digitCount++;
      } else {
        const c = cls.length === 1 ? cls : firstAllowed(cls, [...LETTERS, ...DIGITS]);
        out += c;
        if (/\d/.test(c)) digitCount++;
      }
    }
  }
  if (!out) return null;
  if (variant > 0) {
    // Vary the last digit so example rows don't collide with each other.
    const idx = out.search(/\d(?!.*\d)/);
    if (idx >= 0) {
      const next = String((Number(out[idx]) + variant) % 10);
      const varied = out.slice(0, idx) + next + out.slice(idx + 1);
      if (matchesIdentifierRegex(regex, varied)) return varied;
    }
  }
  return matchesIdentifierRegex(regex, out) ? out : null;
};

/** Example for the template: a regex-based guess, else a readable placeholder. */
export const exampleIdentifierValue = (
  item: { displayName: string; validationRegex: string | null },
  variant = 0
): string =>
  exampleFromRegex(item.validationRegex, variant) ??
  `Example ${item.displayName} ${variant + 1}`;
